// The OmniMedia capability model — the single source of truth (CB-140).
//
// Extracted verbatim from artifacts/api-server/src/lib/capabilities.ts (the
// historical authority). Consumed via thin surface shims:
//   - artifacts/api-server/src/lib/capabilities.ts  (adds express middleware)
//   - artifacts/web/src/lib/capabilities.ts         (adds route-gating UI helpers)
//   - artifacts/mobile/lib/capabilities.ts          (adds workspace-object helpers)
// The OpenAPI `Capability` enum (lib/api-spec/openapi.yaml) and the generated
// client const are pinned to this list by
// artifacts/api-server/src/test/unit/capability-parity.test.ts.
//
// Pure TS only: no express, no DB, no react — importable everywhere and
// runnable under node --experimental-strip-types.

/**
 * The four playback channels. Mirrors the DB `channel_type` enum
 * (lib/db/src/schema/enums.ts); the api-server shim pins the two lists
 * against each other at compile time.
 */
export const CHANNEL_TYPES = [
  "music",
  "ads",
  "announcements",
  "video",
] as const;

export type ChannelType = (typeof CHANNEL_TYPES)[number];

export interface MemberScopes {
  locationIds: string[];
  deviceIds: string[];
  channelTypes: ChannelType[];
  workspaceWide: boolean;
}

export interface EffectivePermissions {
  capabilities: AnyCapability[];
  scopes: MemberScopes;
  isWorkspaceCreator: boolean;
  isBillingAccountHolder: boolean;
  hasImplicitAll: boolean;
  permissionGroupId: string | null;
  permissionGroupName: string | null;
}

/** Assignable via permission groups (manage.billing is ownerUserId-only). */
export const CAPABILITIES = [
  "view.dashboard",
  "view.logs",
  "view.billing",
  "control.devices",
  "broadcast.live",
  "broadcast.announce",
  "control.remote_desktop",
  "manage.locations",
  "manage.fleet",
  "manage.media",
  "manage.playlists.music",
  "manage.playlists.ads",
  "manage.playlists.announcements",
  "manage.playlists.video",
  "manage.schedules",
  "manage.members",
  "manage.settings",
  "manage.support",
  // Ad-rental marketplace. ad_inventory = host side (list a venue for rent,
  // approve/reject incoming bookings, manage the payout account, view
  // earnings). ad_campaigns = advertiser side (browse inventory, create a
  // booking, upload creative, pay, view the report). Both are workspace-wide
  // admin capabilities (not location/device-scoped) for the managed MVP.
  "manage.ad_inventory",
  "manage.ad_campaigns",
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export const BILLING_CAPABILITY = "manage.billing" as const;
export type BillingCapability = typeof BILLING_CAPABILITY;

export type AnyCapability = Capability | BillingCapability;

export const PLAYLIST_CAPABILITIES: Record<ChannelType, Capability> = {
  music: "manage.playlists.music",
  ads: "manage.playlists.ads",
  announcements: "manage.playlists.announcements",
  video: "manage.playlists.video",
};

export const SCOPED_CAPABILITIES = new Set<Capability>([
  "control.devices",
  "broadcast.live",
  "broadcast.announce",
  "control.remote_desktop",
  "manage.locations",
  "manage.fleet",
  "manage.media",
  "manage.playlists.music",
  "manage.playlists.ads",
  "manage.playlists.announcements",
  "manage.playlists.video",
  "manage.schedules",
  "view.logs",
]);

/** Empty scope denies operate/broadcast (floor-control personas). */
export const DENY_ON_EMPTY_SCOPE = new Set<Capability>([
  "control.devices",
  "broadcast.live",
  "broadcast.announce",
  "control.remote_desktop",
]);

export const ALL_GROUP_CAPABILITIES: Capability[] = [...CAPABILITIES];

export const PLAYLIST_MANAGE_CAPABILITIES: Capability[] = [
  "manage.playlists.music",
  "manage.playlists.ads",
  "manage.playlists.announcements",
  "manage.playlists.video",
];

export function playlistCapabilityForChannel(
  channelType: ChannelType,
): Capability {
  return PLAYLIST_CAPABILITIES[channelType];
}

export function hasCapabilityInSet(
  capabilities: readonly string[],
  capability: AnyCapability,
): boolean {
  return capabilities.includes(capability);
}

export function capabilitiesForImplicitAll(
  includeBilling: boolean,
): AnyCapability[] {
  if (includeBilling) return [...ALL_GROUP_CAPABILITIES, BILLING_CAPABILITY];
  return [...ALL_GROUP_CAPABILITIES];
}

export function capabilitiesForOwner(): AnyCapability[] {
  return capabilitiesForImplicitAll(true);
}

export function memberViewCapabilities(groupCaps: string[]): Capability[] {
  const filtered = groupCaps.filter((c): c is Capability =>
    (CAPABILITIES as readonly string[]).includes(c),
  );
  return [...new Set<Capability>([...filtered, "view.billing"])];
}

/**
 * Capabilities in `requested` that `actor` does not itself hold — the set that
 * would be a privilege escalation to grant. A non-owner may never grant a
 * capability beyond their own (mints a service key, edits a permission group,
 * or assigns a member to a group). Owners (`hasImplicitAll`) can grant anything,
 * so this is always empty for them.
 */
export function escalatingCapabilities(
  actor: EffectivePermissions,
  requested: readonly string[],
): string[] {
  if (actor.hasImplicitAll) return [];
  const held = new Set<string>(actor.capabilities);
  return requested.filter((c) => !held.has(c));
}
